<template>
  <div class="flex-col flex gap-4">
    <div class="border-b border-linear-subtle pb-3 flex items-center justify-between">
      <LabelReset
        id="table-form-reset-label"
        :initialValue="initialTable"
        :modelValue="modelValue"
        @update:modelValue="resetTableFormToInitialTable"
      >
        Table Form
      </LabelReset>
      <div class="flex items-center justify-end gap-3">
        <slot name="actions" />
      </div>
    </div>

    <ButtonGroup
      id="standing-room-options"
      v-if="!hideStandingRoomSwitch && hasStandingRoom"
      :options="STANDING_ROOM_OPTIONS"
      v-model="tableForm.standing_room"
      itemText="text"
      itemValue="value"
    />
    <div class="flex justify-start items-start">
      <div class="w-1/2 pr-2">
        <TextInput
          id="name-input"
          label="Table Name"
          placeholder="Name"
          class="mb-2"
          :initialValue="initialTableValues.name"
          v-model.trim="tableForm.name"
        />
      </div>
      <div class="w-1/2 pl-2">
        <NumberInput
          id="price-input"
          :initialValue="initialTableValues.price_per_person"
          v-model="tableForm.price_per_person"
          step=".01"
          min="0"
          label="Price Per Person"
          currency
          class="mb-2"
        />
      </div>
    </div>
    <div class="flex justify-start items-start">
      <div v-if="!isStandingRoom" class="w-1/2 pr-2">
        <NumberInput
          :initialValue="initialTableValues.minimum_guests"
          v-model="tableForm.minimum_guests"
          id="min-guest-input"
          label="Min Guests"
          min="0"
        />
      </div>
      <div :class="{ 'p-0 w-full': isStandingRoom, 'pl-2 w-1/2': !isStandingRoom }">
        <NumberInput
          id="max-guest-input"
          :initialValue="initialTableValues.maximum_guests"
          v-model="tableForm.maximum_guests"
          label="Max Guests"
          min="0"
        />
      </div>
    </div>
    <div class="flex justify-start items-start">
      <div class="w-1/2 pr-2">
        <PerformanceTableShapeToggle
          id="shape-input"
          :initialShape="initialTableValues.shape"
          v-model="tableForm.shape"
        />
      </div>
      <div class="w-1/2 pl-2">
        <NumberInput
          id="rotation-input"
          :initialValue="initialTableValues.rotation"
          v-model="tableForm.rotation"
          label="Rotation"
          min="0"
          max="360"
          step="15"
        />
      </div>
    </div>
    <div class="flex justify-start items-start">
      <div class="w-1/2 pr-2">
        <NumberInput
          id="width-input"
          :initialValue="initialTableValues.w"
          v-model="tableForm.w"
          :label="widthLabel"
          :min="isStandingRoom ? '1' : '0.5'"
          :max="isStandingRoom ? '50' : '10'"
          :step="isStandingRoom ? '1' : '0.5'"
        />
      </div>
      <div class="w-1/2 pl-2">
        <div v-if="!usesDiameter" class="relative">
          <span
            class="cursor-pointer text-fg-link-primary font-sans text-sm absolute top-0 left-1/2 -translate-y-1"
            id="width-height-equal-button"
            @click="isWidthAndHeightEqual = !isWidthAndHeightEqual"
          >
            {{ isWidthAndHeightEqual ? 'Equal' : 'Unequal' }}
          </span>
          <NumberInput
            :initialValue="initialTableValues.h"
            v-model="tableForm.h"
            label="Height"
            id="height-input"
            :min="isStandingRoom ? '1' : '0.5'"
            :max="isStandingRoom ? '50' : '10'"
            :step="isStandingRoom ? '1' : '0.5'"
          />
        </div>
      </div>
    </div>
    <div v-if="!isStandingRoom" class="flex justify-start items-start">
      <PerformanceTableTypeRadioGroup
        v-model="tableForm.type_id"
        :initialValue="initialTableValues.type_id"
        class="w-1/2 pr-2"
      />
      <PerformanceHeldRadioGroup
        v-model="tableForm.held"
        :initialValue="initialTableValues.held"
        class="w-1/2 pr-2"
      />
    </div>
    <LabelReset v-if="hasPositionChanged" :initialValue="initialPosition" v-model="position">
      Position has changed
    </LabelReset>
  </div>
</template>

<script lang="ts" setup>
import * as _ from 'lodash-es'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, toRefs, watch } from 'vue'

import ButtonGroup from '@/common/components/molecules/ButtonGroup.vue'
import LabelReset from '@/common/components/molecules/LabelReset.vue'
import NumberInput from '@/common/components/molecules/NumberInput.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import PerformanceHeldRadioGroup from '@/modules/performance/components/molecules/PerformanceHeldRadioGroup.vue'
import PerformanceTableShapeToggle from '@/modules/performance/components/molecules/PerformanceTableShapeToggle.vue'
import PerformanceTableTypeRadioGroup from '@/modules/performance/components/molecules/PerformanceTableTypeRadioGroup.vue'
import { useSettingStore } from '@/stores/setting'

import { FeatureFlag, PerformanceTableRead, TableShape } from '@generated/types'

type TableForm = Pick<
  PerformanceTableRead,
  | 'name'
  | 'price_per_person'
  | 'maximum_guests'
  | 'minimum_guests'
  | 'shape'
  | 'rotation'
  | 'w'
  | 'h'
  | 'type_id'
  | 'x'
  | 'y'
  | 'standing_room'
  | 'held'
>

const STANDING_ROOM_OPTIONS = [
  { text: 'Table', value: false },
  { text: 'Standing Room', value: true },
]

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (e: 'update:modelValue', value: PerformanceTableRead): void
}>()

const props = withDefaults(
  defineProps<{
    initialTable?: PerformanceTableRead
    modelValue?: PerformanceTableRead
    hideStandingRoomSwitch?: boolean
  }>(),
  {
    initialTable: undefined,
    modelValue: undefined,
    hideStandingRoomSwitch: false,
  },
)
const { modelValue, initialTable } = toRefs(props)

const settingStore = useSettingStore()
const { table_types } = storeToRefs(settingStore)

const tableForm = ref<TableForm>({
  name: 'New table',
  price_per_person: 0,
  minimum_guests: 0,
  maximum_guests: 0,
  shape: TableShape.SQUARE,
  rotation: 0,
  w: 4,
  h: 4,
  type_id: 0,
  x: 0,
  y: 0,
  standing_room: false,
  held: false,
})
onMounted(async () => {
  await settingStore.loading
  if (table_types.value.length > 0) {
    tableForm.value.type_id = table_types.value[0].id
  }
  updateTableFormFromState()
})
// When table fires changes externally, update the form
watch(() => modelValue.value, updateTableFormFromState, { deep: true })
// When form fires changes, update the table
watch(
  tableForm,
  newTable => {
    if (!modelValue.value) return
    const updatedTable: PerformanceTableRead = {
      ...modelValue.value,
      ...newTable,
      price_per_person: newTable.price_per_person.toFixed(2),
    }
    if (_.isEqual(updatedTable, modelValue.value)) {
      return
    }
    emit('update:modelValue', updatedTable)
  },
  { deep: true },
)
function updateTableFormFromState() {
  if (!modelValue.value) return
  tableForm.value = {
    ...modelValue.value,
    price_per_person: Number(modelValue.value.price_per_person),
  }
}
function resetTableFormToInitialTable() {
  if (!initialTable.value) return
  tableForm.value = {
    ...initialTable.value,
    price_per_person: Number(initialTable.value.price_per_person),
  }
}
const initialTableValues = computed<Omit<Partial<TableForm>, 'standing_room'>>(function () {
  if (!initialTable.value) return {}
  return {
    ...initialTable.value,
    price_per_person: Number(initialTable.value.price_per_person),
  }
})

const isWidthAndHeightEqual = ref<boolean>(false)
watch(isWidthAndHeightEqual, newValue => {
  if (newValue) tableForm.value.h = tableForm.value.w
})
watch(
  () => tableForm.value.w,
  value => {
    if (isWidthAndHeightEqual.value) tableForm.value.h = value
  },
)
watch(
  () => tableForm.value.h,
  value => {
    if (isWidthAndHeightEqual.value) tableForm.value.w = value
  },
)
watch(
  () => tableForm.value.standing_room,
  newValue => {
    if (!newValue) {
      if (tableForm.value.w > 10) tableForm.value.w = 10
      if (tableForm.value.h > 10) tableForm.value.h = 10
    }
  },
)

const usesDiameter = computed<boolean>(function () {
  return (
    tableForm.value.shape == TableShape.CIRCLE || tableForm.value.shape == TableShape.HALF_CIRCLE
  )
})
const widthLabel = computed<string>(function (): string {
  return usesDiameter.value ? 'Diameter' : 'Width'
})
const isStandingRoom = computed<boolean>(function () {
  return tableForm.value.standing_room
})
const hasStandingRoom = computed<boolean>(function () {
  return settingStore.flagEnabled(FeatureFlag.STANDING_ROOM)
})

const initialPosition = computed<{ x: number; y: number } | undefined>(() => {
  if (!initialTable.value) return undefined
  return { x: initialTable.value.x, y: initialTable.value.y }
})
const position = computed<{ x: number; y: number }>({
  get: () => ({ x: tableForm.value.x, y: tableForm.value.y }),
  set: value => {
    tableForm.value.x = value.x
    tableForm.value.y = value.y
  },
})
const hasPositionChanged = computed<boolean>(function () {
  if (!initialPosition.value) return false
  return (
    initialPosition.value.x !== position.value.x || initialPosition.value.y !== position.value.y
  )
})
</script>
